import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Projects — move work from idea to PROOF. A Project is the one canonical home
 * for a body of work; tasks, targets, notes, and sources link TO it rather than
 * duplicating it. It deliberately carries a single "next action" (to stay calm
 * and avoid planning theatre) and a "definition of done" (so "proof" is explicit
 * and shipping is unambiguous, not vibes).
 */

export type ProjectStatus = 'idea' | 'active' | 'paused' | 'shipped' | 'archived';
export const PROJECT_STATUSES: readonly ProjectStatus[] = [
  'idea',
  'active',
  'paused',
  'shipped',
  'archived',
] as const;

export interface Project extends RecordEnvelope {
  readonly kind: 'project';
  readonly title: string;
  readonly summary: string;
  readonly status: ProjectStatus;
  /** The single next move. Empty means the project needs a decision, not more tasks. */
  readonly nextAction: string;
  /** What "proof"/done looks like — the bar that lets it ship honestly. */
  readonly definitionOfDone: string;
  /** Linked tasks/targets/notes/sources (ids only, no duplication). */
  readonly linkedIds: readonly EntityId[];
}

export interface ProjectInput {
  readonly title: string;
  readonly summary?: string;
  readonly nextAction?: string;
  readonly definitionOfDone?: string;
  readonly privacyClass?: PrivacyClass;
}

export interface ProjectPatch {
  readonly summary?: string;
  readonly nextAction?: string;
  readonly definitionOfDone?: string;
}

/** Active and idea projects are "in motion"; paused/shipped/archived are at rest. */
export function isProjectInMotion(status: ProjectStatus): boolean {
  return status === 'idea' || status === 'active';
}

/**
 * A project is "stalled" when it is meant to be moving but has no next action.
 * This is a neutral nudge to decide, never a productivity scold.
 */
export function isProjectStalled(project: Pick<Project, 'status' | 'nextAction'>): boolean {
  return project.status === 'active' && project.nextAction.trim().length === 0;
}

export interface PortfolioSummary {
  readonly total: number;
  readonly active: number;
  readonly shipped: number;
  readonly stalled: number;
  readonly byStatus: Record<ProjectStatus, number>;
}

/** Pure, neutral portfolio roll-up for the Projects screen header. */
export function summarizeProjects(
  projects: readonly Pick<Project, 'status' | 'nextAction'>[],
): PortfolioSummary {
  const byStatus: Record<ProjectStatus, number> = {
    idea: 0,
    active: 0,
    paused: 0,
    shipped: 0,
    archived: 0,
  };
  let stalled = 0;
  for (const p of projects) {
    byStatus[p.status] += 1;
    if (isProjectStalled(p)) stalled += 1;
  }
  return {
    total: projects.length,
    active: byStatus.active,
    shipped: byStatus.shipped,
    stalled,
    byStatus,
  };
}
